import DataTable from "../../components/common/DataTable";
import { Search, X } from "lucide-react";
import { Avatar, Button, TextInput, Switch, Text, Group, ActionIcon } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Head from "../../components/common/Head";
import useValidate from "../../hooks/useValidate";
import { deleteCreator, getAllCreators, updateCreator } from "../../utils/api";
import useStore from "../../utils/store";

const Creators = () => {
  const navigate = useNavigate();

  const [creators, setCreators] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingStates, setLoadingStates] = React.useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputText, setInputText] = React.useState(searchParams.get("search") || "");
  const [searchText, setSearchText] = React.useState(searchParams.get("search") || "");
  const [pagination, setPagination] = React.useState({
    current: parseInt(searchParams.get("page") || "1"),
    pageSize: 10,
    total: 0,
  });

  const user = useStore((state) => state.user);

  const fetchCreators = async (page = pagination.current, pageSize = pagination.pageSize, search = searchText) => {
    setLoading(true);
    const [success, response] = await getAllCreators(page, pageSize, search);
    if (success) {
      setCreators(response.data || []);
      setPagination((prev) => ({ ...prev, current: response.metadata?.currentPage || 1, total: response.metadata?.totalData || 0, pageSize }));
    }
    setLoading(false);
  };

  const handleStatusChange = async (value, record) => {
    setLoadingStates((prev) => ({ ...prev, [record._id]: true }));
    const [success] = await updateCreator(record._id, { isDisable: !value });
    if (success) {
      setCreators(creators.map((c) => c._id === record._id ? { ...c, isDisable: !value } : c));
      notifications.show({ message: "Creator status updated", color: "green" });
    } else {
      notifications.show({ message: "Failed to update status", color: "red" });
    }
    setLoadingStates((prev) => ({ ...prev, [record._id]: false }));
  };

  const handleVerificationChange = async (value, record) => {
    setLoadingStates((prev) => ({ ...prev, [record._id]: true }));
    const [success] = await updateCreator(record._id, { isVerified: value });
    if (success) {
      setCreators(creators.map((c) => c._id === record._id ? { ...c, isVerified: value } : c));
      notifications.show({ message: "Verification status updated", color: "green" });
    } else {
      notifications.show({ message: "Failed to update verification", color: "red" });
    }
    setLoadingStates((prev) => ({ ...prev, [record._id]: false }));
  };

  const handleFeaturedChange = async (value, record) => {
    setLoadingStates((prev) => ({ ...prev, [record._id]: true }));
    const [success] = await updateCreator(record._id, { isFeatured: value });
    if (success) {
      setCreators(creators.map((c) => c._id === record._id ? { ...c, isFeatured: value } : c));
      notifications.show({ message: "Featured status updated", color: "green" });
    } else {
      notifications.show({ message: "Failed to update featured status", color: "red" });
    }
    setLoadingStates((prev) => ({ ...prev, [record._id]: false }));
  };

  const showDeleteConfirm = (id, name) => {
    modals.openConfirmModal({
      title: `Delete creator "${name}"?`,
      children: <Text size="sm">This action cannot be undone.</Text>,
      labels: { confirm: "Yes, Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        const [success] = await deleteCreator(id);
        if (success) {
          notifications.show({ message: "Creator deleted successfully", color: "green" });
          fetchCreators();
        } else {
          notifications.show({ message: "Failed to delete creator", color: "red" });
        }
      },
    });
  };

  const handleTableChange = (pag) => {
    setPagination({ current: pag.current, pageSize: pag.pageSize });
    setSearchParams({ page: pag.current.toString(), ...(searchText && { search: searchText }) });
    fetchCreators(pag.current, pag.pageSize, searchText);
  };

  const handleSearch = () => {
    setSearchText(inputText);
    setPagination((p) => ({ ...p, current: 1 }));
    setSearchParams({ page: "1", ...(inputText && { search: inputText }) });
    fetchCreators(1, pagination.pageSize, inputText);
  };

  const handleSearchClear = () => {
    setInputText("");
    setSearchText("");
    setPagination((p) => ({ ...p, current: 1 }));
    setSearchParams({ page: "1" });
    fetchCreators(1, pagination.pageSize, "");
  };

  useValidate();

  const hasPermission = React.useMemo(() => user?.getAllCreators || false, [user]);
  const getPermissions = React.useMemo(() => ({
    addCreator: user?.addCreators || false,
    editCreator: user?.editCreators || false,
    deleteCreator: user?.deleteCreators || false,
    viewDashboard: user?.getVerifiedCreators || false,
  }), [user]);

  React.useEffect(() => {
    if (hasPermission) fetchCreators(pagination.current, pagination.pageSize, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  return (
    <div className="flex flex-col gap-4">
      <Head
        title="Partners"
        customButton={
          getPermissions.addCreator ? (
            <Button onClick={() => navigate("/partners/add")}>Add Partner</Button>
          ) : null
        }
      />
      {!hasPermission ? (
        <Text c="dimmed" ta="center" py="xl">You don't have permission to view partners</Text>
      ) : (
        <>
          <Group gap="xs" mb="md">
            <TextInput
              placeholder="Search creators by name, email..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              style={{ width: 360 }}
              rightSection={
                inputText ? (
                  <ActionIcon variant="subtle" onClick={handleSearchClear} size="sm">
                    <X size={14} />
                  </ActionIcon>
                ) : null
              }
            />
            <Button leftSection={<Search size={16} />} onClick={handleSearch}>Search</Button>
          </Group>
          <DataTable
            loading={loading}
            dataSource={creators}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={handleTableChange}
            columns={[
              { title: "Profile", dataIndex: "image", key: "profilePic", render: (src) => <Avatar src={src} /> },
              { title: "Name", dataIndex: "name", key: "name" },
              { title: "Email", dataIndex: "email", key: "email" },
              { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
              {
                title: "Status",
                dataIndex: "isDisable",
                key: "isDisable",
                render: (isDisable, record) => (
                  <Switch
                    checked={!isDisable}
                    label={!isDisable ? "Enabled" : "Disabled"}
                    disabled={loadingStates[record._id]}
                    onChange={(e) => handleStatusChange(e.currentTarget.checked, record)}
                  />
                ),
              },
              {
                title: "Verified",
                dataIndex: "isVerified",
                key: "isVerified",
                render: (isVerified, record) => (
                  <Switch
                    checked={isVerified}
                    label={isVerified ? "Verified" : "Not Verified"}
                    disabled={loadingStates[record._id]}
                    onChange={(e) => handleVerificationChange(e.currentTarget.checked, record)}
                  />
                ),
              },
              {
                title: "Featured",
                dataIndex: "isFeatured",
                key: "isFeatured",
                render: (isFeatured, record) => (
                  <Switch
                    checked={isFeatured || false}
                    label={isFeatured ? "Featured" : "Not Featured"}
                    disabled={loadingStates[record._id]}
                    onChange={(e) => handleFeaturedChange(e.currentTarget.checked, record)}
                  />
                ),
              },
              {
                title: "Actions",
                key: "actions",
                render: (_, record) => (
                  <Group gap="xs">
                    {getPermissions.editCreator && (
                      <Button size="xs" onClick={() => navigate(`/partners/edit/${record._id}`)}>Edit</Button>
                    )}
                    {getPermissions.viewDashboard && (
                      <>
                        <Button size="xs" variant="outline" onClick={() => navigate(`/partners/dashboard/${record._id}`)}>Dashboard</Button>
                        <Button size="xs" variant="outline" onClick={() => navigate(`/partners/revenue/${record._id}`)}>Revenue</Button>
                      </>
                    )}
                    {getPermissions.deleteCreator && (
                      <Button size="xs" color="red" variant="light" onClick={() => showDeleteConfirm(record._id, record.name)}>Delete</Button>
                    )}
                  </Group>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default Creators;
